import React, { FC, useEffect, useState } from 'react';
import Departments from 'models/departments';
import DepartmentText from './DepartmentText';
import { departmentSelectedEvent } from '../../common/models/TrackingEvents';
import { track } from '../../helpers/tracking/tracking';

interface DepartmentBoxProps {
  context: string;
  department: string;
  fontSize?: string;
  getDepartmentLink?: (department: string) => string;
  handleClick?: (department: string, e?: any) => void;
  handleHover?: (department: string) => void;
  initialDepartment?: string;
  resetDepartment?: boolean;
  underline: boolean;
}

const DepartmentBox: FC<DepartmentBoxProps> = ({
  department,
  underline,
  fontSize,
  context,
  handleHover,
  resetDepartment,
  initialDepartment,
  handleClick,
  getDepartmentLink,
}) => {
  const [hoverIndex, setHoverIndex] = useState(null);
  const [reset, setReset] = useState(resetDepartment);

  useEffect(() => {
    if (typeof resetDepartment !== 'undefined' && reset !== resetDepartment) {
      setHoverIndex(initialDepartment);
      setReset(resetDepartment);

      if (handleHover) {
        handleHover(initialDepartment);
      } else {
        handleClick(initialDepartment);
      }
    }
  }, [resetDepartment, handleHover, handleClick, initialDepartment, reset]);

  const isActive = (index: string): boolean => {
    return !hoverIndex ? index === Departments.getInternalName(department) : index === hoverIndex;
  };

  const selectDepartment = (selectedDepartment: string, e: any): void => {
    track(departmentSelectedEvent, {
      categoryDepartment: { department: selectedDepartment.toLowerCase() },
      ui: { context },
    });
    handleClick(selectedDepartment, e);
  };

  const changeHoverIndex = (index: string): void => {
    if (context && context === 'search') {
      return;
    }
    setHoverIndex(index);

    if (handleHover) {
      handleHover(index);
    }
  };

  return (
    <>
      <a className="fallback-link" href={getDepartmentLink && getDepartmentLink(Departments.getWomenInternalName())}>
        <DepartmentText
          active={isActive(Departments.getWomenInternalName())}
          data-cy={`input-link-${context}-women`}
          fontSize={fontSize}
          onClick={e => selectDepartment(Departments.getWomenInternalName(), e)}
          onFocus={() => {}}
          onMouseEnter={() => {
            if (handleHover) {
              changeHoverIndex(Departments.getWomenInternalName());
            }
          }}
          title={Departments.getWomenName()}
          underline={underline}
        >
          {Departments.getWomenName()}
        </DepartmentText>
      </a>
      <a
        className="fallback-link"
        href={getDepartmentLink && getDepartmentLink(Departments.getMenInternalName())}
        style={{ margin: '0 36px' }}
      >
        <DepartmentText
          active={isActive(Departments.getMenInternalName())}
          data-cy={`input-link-${context}-men`}
          fontSize={fontSize}
          onClick={e => selectDepartment(Departments.getMenInternalName(), e)}
          onFocus={() => {}}
          onMouseEnter={() => {
            if (handleHover) {
              changeHoverIndex(Departments.getMenInternalName());
            }
          }}
          title={Departments.getMenName()}
          underline={underline}
        >
          {Departments.getMenName()}
        </DepartmentText>
      </a>
      <a className="fallback-link" href={getDepartmentLink && getDepartmentLink(Departments.getKidsInternalName())}>
        <DepartmentText
          active={isActive(Departments.getKidsInternalName())}
          data-cy={`input-link-${context}-kids`}
          fontSize={fontSize}
          onClick={e => selectDepartment(Departments.getKidsInternalName(), e)}
          onFocus={() => {}}
          onMouseEnter={() => {
            if (handleHover) {
              changeHoverIndex(Departments.getKidsInternalName());
            }
          }}
          title={Departments.getKidsName()}
          underline={underline}
        >
          {Departments.getKidsName()}
        </DepartmentText>
      </a>

      <style jsx>{`
        .fallback-link {
          display: block;
        }

        @media (max-width: 1023px) {
          .fallback-link:hover {
            text-decoration: none;
          }
        }
      `}</style>
    </>
  );
};

export default DepartmentBox;
